import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  LinearProgress,
  Alert,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  PhotoLibrary,
  Send,
} from '@mui/icons-material';
import { usePosts } from '../../context/posts.context';
import { useSnackbar } from '../../context/snackbar.context';
import { MEAL_TYPE_LABELS } from '../../data/exercises.data';

/**
 * Dialog for creating a meal/nutrition post.
 * Single-page form with meal type, title, photo, macros, ingredients, recipe, and caption.
 */
const CreateMealPost = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { createPost, submitting, mealPostOpen, closeMealPost } = usePosts();
  const { showSnackbar } = useSnackbar();

  const [error, setError] = useState(null);

  // Meal fields
  const [mealTitle, setMealTitle] = useState('');
  const [mealType, setMealType] = useState('lunch');
  const [calories, setCalories] = useState('');
  const [protein, setProtein] = useState('');
  const [carbs, setCarbs] = useState('');
  const [fat, setFat] = useState('');
  const [fiber, setFiber] = useState('');
  const [ingredients, setIngredients] = useState([]);
  const [newIngredient, setNewIngredient] = useState('');
  const [recipe, setRecipe] = useState('');
  const [caption, setCaption] = useState('');

  // Image
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const canSubmit = mealTitle.trim().length > 0 && !submitting;

  // --- Ingredient management ---
  const handleAddIngredient = () => {
    if (newIngredient.trim()) {
      setIngredients((prev) => [...prev, newIngredient.trim()]);
      setNewIngredient('');
    }
  };

  const handleRemoveIngredient = (index) => {
    setIngredients((prev) => prev.filter((_, i) => i !== index));
  };

  const handleIngredientKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddIngredient();
    }
  };

  // --- Image handling ---
  const handleImageSelect = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please select a JPEG, PNG, GIF, or WebP image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image is too large. Maximum size is 5MB.');
      return;
    }
    setError(null);
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
    event.target.value = '';
  }, []);

  const handleRemoveImage = useCallback(() => {
    setImageFile(null);
    setImagePreview(null);
    setUploadProgress(0);
  }, []);

  // --- Submit ---
  const handleSubmit = useCallback(async () => {
    setError(null);
    setUploadProgress(0);
    try {
      const mealData = {
        title: mealTitle.trim(),
        mealType,
        calories: parseInt(calories, 10) || 0,
        macros: {
          protein: parseInt(protein, 10) || 0,
          carbs: parseInt(carbs, 10) || 0,
          fat: parseInt(fat, 10) || 0,
          fiber: parseInt(fiber, 10) || 0,
        },
        ingredients,
        recipe: recipe.trim() || null,
      };

      await createPost({
        type: 'meal',
        text: caption.trim(),
        imageFile: imageFile || undefined,
        onImageProgress: (progress) => setUploadProgress(progress),
        meal: mealData,
      });

      resetForm();
      closeMealPost();
      showSnackbar('Meal logged!', 'success');
    } catch (err) {
      setError(err.message || 'Failed to create meal post.');
    }
  }, [mealTitle, mealType, calories, protein, carbs, fat, fiber, ingredients, recipe, caption, imageFile, createPost, closeMealPost, showSnackbar]);

  const resetForm = () => {
    setMealTitle('');
    setMealType('lunch');
    setCalories('');
    setProtein('');
    setCarbs('');
    setFat('');
    setFiber('');
    setIngredients([]);
    setNewIngredient('');
    setRecipe('');
    setCaption('');
    setImageFile(null);
    setImagePreview(null);
    setUploadProgress(0);
    setError(null);
  };

  const handleClose = useCallback(() => {
    if (submitting) return;
    resetForm();
    closeMealPost();
  }, [submitting, closeMealPost]);

  return (
    <Dialog
      open={mealPostOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      aria-labelledby="create-meal-dialog-title"
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" p={2} pb={0}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }} id="create-meal-dialog-title">
          Log Meal
        </Typography>
        <IconButton edge="end" onClick={handleClose} disabled={submitting} aria-label="Close dialog">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Meal type selection */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Meal Type</InputLabel>
          <Select
            value={mealType}
            onChange={(e) => setMealType(e.target.value)}
            label="Meal Type"
          >
            {Object.entries(MEAL_TYPE_LABELS).map(([key, label]) => (
              <MenuItem key={key} value={key}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Title */}
        <TextField
          label="Meal Title"
          placeholder='e.g., "Protein Bowl", "Post-Workout Shake"'
          value={mealTitle}
          onChange={(e) => setMealTitle(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />

        {/* Photo */}
        <input
          ref={fileInputRef}
          hidden
          accept="image/jpeg,image/png,image/gif,image/webp"
          type="file"
          onChange={handleImageSelect}
        />

        {imagePreview ? (
          <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', mb: 2 }}>
            <Box
              component="img"
              src={imagePreview}
              alt="Meal preview"
              sx={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 2, display: 'block' }}
            />
            <IconButton
              onClick={handleRemoveImage}
              disabled={submitting}
              sx={{
                position: 'absolute', top: 8, right: 8,
                backgroundColor: 'rgba(0,0,0,0.6)', color: 'white',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
              }}
              size="small"
              aria-label="Remove image"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <Button
            startIcon={<PhotoLibrary />}
            onClick={() => fileInputRef.current?.click()}
            variant="outlined"
            sx={{ textTransform: 'none', mb: 2 }}
          >
            Add Photo
          </Button>
        )}

        {/* Macro inputs */}
        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
          Nutrition Info
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Calories"
            value={calories}
            onChange={(e) => setCalories(e.target.value.replace(/\D/g, ''))}
            size="small"
            sx={{ width: 90 }}
            inputMode="numeric"
          />
          <TextField
            label="Protein (g)"
            value={protein}
            onChange={(e) => setProtein(e.target.value.replace(/\D/g, ''))}
            size="small"
            sx={{ width: 90 }}
            inputMode="numeric"
          />
          <TextField
            label="Carbs (g)"
            value={carbs}
            onChange={(e) => setCarbs(e.target.value.replace(/\D/g, ''))}
            size="small"
            sx={{ width: 90 }}
            inputMode="numeric"
          />
          <TextField
            label="Fat (g)"
            value={fat}
            onChange={(e) => setFat(e.target.value.replace(/\D/g, ''))}
            size="small"
            sx={{ width: 80 }}
            inputMode="numeric"
          />
          <TextField
            label="Fiber (g)"
            value={fiber}
            onChange={(e) => setFiber(e.target.value.replace(/\D/g, ''))}
            size="small"
            sx={{ width: 80 }}
            inputMode="numeric"
          />
        </Box>

        {/* Ingredients */}
        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
          Ingredients
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField
            label="Add ingredient"
            value={newIngredient}
            onChange={(e) => setNewIngredient(e.target.value)}
            onKeyDown={handleIngredientKeyDown}
            size="small"
            fullWidth
          />
          <IconButton
            onClick={handleAddIngredient}
            disabled={!newIngredient.trim()}
            color="primary"
            aria-label="Add ingredient"
          >
            <AddIcon />
          </IconButton>
        </Box>
        {ingredients.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
            {ingredients.map((ingredient, index) => (
              <Chip
                key={index}
                label={ingredient}
                onDelete={() => handleRemoveIngredient(index)}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        )}

        {/* Recipe */}
        <TextField
          label="Recipe (optional)"
          value={recipe}
          onChange={(e) => setRecipe(e.target.value)}
          fullWidth
          multiline
          rows={3}
          sx={{ mb: 2 }}
        />

        {/* Caption */}
        <TextField
          label="Caption (optional)"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          fullWidth
          multiline
          rows={2}
          sx={{ mb: 1 }}
        />

        {/* Upload progress */}
        {submitting && imageFile && uploadProgress > 0 && uploadProgress < 100 && (
          <Box sx={{ mt: 1 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <Typography variant="caption" color="text.secondary">
              Uploading image... {uploadProgress}%
            </Typography>
          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <Box display="flex" justifyContent="flex-end" p={2}>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          variant="contained"
          endIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <Send />}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          {submitting ? 'Posting...' : 'Post Meal'}
        </Button>
      </Box>
    </Dialog>
  );
};

export default CreateMealPost;
